<template>
  <router-link to="/" class="home-button">返回首页</router-link>
  <div class="price-query">
    <!-- 查询区域 -->
    <el-form :inline="true" class="filter-form" label-width="80px">
      <el-form-item label="种类">
        <el-select
            v-model="filters.categoryId"
            placeholder="请选择种类"
            @change="loadProducts"
            style="width: 150px"
            filterable
            clearable
        >
          <el-option
              v-for="item in categoryList"
              :key="item.id"
              :label="item.categoryName"
              :value="item.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="农产品">
        <el-select
            v-model="filters.productId"
            placeholder="请选择农产品"
            style="width: 150px"
            filterable
            clearable
        >
          <el-option
              v-for="item in productList"
              :key="item.id"
              :label="item.productName"
              :value="item.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="产地">
        <el-input
            v-model="filters.place"
            placeholder="请输入产地"
            style="width: 150px"
            clearable
        />
      </el-form-item>

      <el-form-item label="开始时间" :class="{ 'is-error': startDateError }">
        <el-date-picker
            v-model="filters.startDate"
            type="date"
            placeholder="开始日期"
            value-format="YYYY-MM-DD"
            format="YYYY年MM月DD日"
            style="width: 200px"
            :disabled-date="disabledStartDate"
            @change="handleDateChange"
        />
        <span class="error-message" v-if="startDateError">{{ startDateError }}</span>
      </el-form-item>

      <el-form-item label="结束时间" :class="{ 'is-error': endDateError }">
        <el-date-picker
            v-model="filters.endDate"
            type="date"
            placeholder="结束日期"
            value-format="YYYY-MM-DD"
            format="YYYY年MM月DD日"
            style="width: 200px"
            :disabled-date="disabledEndDate"
            @change="handleDateChange"
        />
        <span class="error-message" v-if="endDateError">{{ endDateError }}</span>
      </el-form-item>

      <el-form-item>
        <el-button type="primary" @click="handleQuery">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 表格区域 -->
    <el-table :data="tableData" @cell-click="handleRowMouseEnter" stripe style="width: 100%; margin-top: 20px">
      <el-table-column prop="productName" label="产品名称" width="80" />
      <el-table-column prop="productId" label="产品ID" width="100" />
      <el-table-column prop="product" label="品种名称" />
      <el-table-column prop="place" label="产地" />
      <el-table-column
          prop="distance"
          label="距离"
          :formatter="(row) => row.distance && row.distance != 'null' ? `${row.distance} 千米` : '未知'"
      />
      <el-table-column prop="time" label="日期" width="120" />
      <el-table-column prop="price" label="价格" width="100" />
    </el-table>

    <!-- 图表展示区域 -->
    <el-dialog
        v-model="showChartDialog"
        title="价格变化趋势"
        width="600px"
        :destroy-on-close="true"
    >
      <div id="priceChart" style="width: 100%; height: 400px"></div>
      <div class="chart-label">
        <span>{{ chartLabel }}</span>
      </div>
    </el-dialog>

    <!-- 分页区域 -->
    <div style="margin-top: 20px; text-align: right">
      <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.size"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.totalCount"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
      />
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, watch, nextTick} from 'vue';
import axios from 'axios';
import {ElMessage} from 'element-plus';
import * as echarts from 'echarts';
import coordtransform from "coordtransform";

const showChartDialog = ref(false);
let chartInstance = null;
let hoverTimeout = null;  // 用来保存定时器ID
// 经度
let longitude = null;
// 维度
let latitude = null;

const categoryList = ref([]);
const productList = ref([]);
const tableData = ref([]);
const chartLabel = ref('');

const filters = ref({
  categoryId: null,
  productId: null,
  startDate: null,
  endDate: null,
  place: null
});

const pagination = ref({
  page: 1,
  size: 10,
  totalCount: 0
});

const startDateError = ref('');
const endDateError = ref('');

// 日期校验
const validateDates = () => {
startDateError.value = '';
endDateError.value = '';

const {startDate, endDate} = filters.value;

if (startDate && endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start.getTime() > end.getTime()) {
    startDateError.value = '开始日期不能大于结束日期';
    endDateError.value = '结束日期不能小于开始日期';
  }
}
};

watch(() => [filters.value.startDate, filters.value.endDate], validateDates, {immediate: true});

const handleDateChange = () => {
  validateDates();
  if (startDateError.value) {
    ElMessage.warning(startDateError.value);
  }
};

const disabledStartDate = (time) => {
  if (!filters.value.endDate) return false;
  const end = new Date(filters.value.endDate);
  end.setHours(0, 0, 0, 0);
  time.setHours(0, 0, 0, 0);
  return time.getTime() > end.getTime();
};

const disabledEndDate = (time) => {
  if (!filters.value.startDate) return false;
  const start = new Date(filters.value.startDate);
  start.setHours(0, 0, 0, 0);
  time.setHours(0, 0, 0, 0);
  return time.getTime() < start.getTime();
};

// 获取用户浏览器定位
const getUserLocation = () =>  {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
        (position) => {
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
          const [gcjLng, gcjLat] = coordtransform.wgs84togcj02(longitude, latitude);
          longitude = gcjLng;
          latitude = gcjLat;
        },
        (error) => {
          console.error('获取位置信息失败:', error);
          this.location = '定位失败';
        },
        {timeout: 10000}
    );
  } else {
    console.error('浏览器不支持地理定位');
    this.location = '定位失败';
  }
};

const handleQuery = () => {
  validateDates();
  if (startDateError.value) {
    ElMessage.error('请修正日期范围后再查询');
    return;
  }
  if (filters.value.categoryId && !filters.value.productId) {
    ElMessage.warning('请选择对应的农产品');
    return;
  }
  pagination.value.page = 1;
  loadTableData();
};

const loadCategories = async () => {
  try {
    const res = await axios.get('/api/farmOnline/category');
    categoryList.value = res.data.data.items.map(item => {
      if (!item.categoryName) item.categoryName = '未知种类';
      return item;
    });
    await loadProducts(); // 加载产品数据
  } catch (error) {
    console.error('获取种类数据失败:', error);
  }
};

const loadProducts = async () => {
  const categoryId = filters.value.categoryId;
  try {
    const res = await axios.get('/api/farmOnline/product', {
      params: {categoryId, pageNum: 0, pageSize: 0}
    });
    productList.value = res.data.data.items.map(item => {
      if (!item.productName) item.productName = '未知产品';
      return item;
    });
    await loadTableData();

  } catch (error) {
    console.error('获取产品数据失败:', error);
  }
};

const loadTableData = async () => {
  try {
    const res = await axios.get('/api/farmOnline/price', {
      params: {
        productId: filters.value.productId,
        place: filters.value.place,
        startDate: filters.value.startDate,
        endDate: filters.value.endDate,
        pageNum: pagination.value.page,
        pageSize: pagination.value.size,
        pos: longitude + ',' + latitude
      }
    });
    tableData.value = res.data.data.items;
    pagination.value.totalCount = res.data.data.totalCount || 0;
  } catch (error) {
    console.error('获取价格数据失败:', error);
  }
};

const handlePageChange = (newPage) => {
  pagination.value.page = newPage;
  loadTableData();
};

const handleSizeChange = (newSize) => {
  pagination.value.size = newSize;
  pagination.value.page = 1;
  loadTableData();
};

const resetFilters = () => {
  filters.value = {
    categoryId: null,
    productId: null,
    startDate: null,
    endDate: null,
    place: null
  };
  productList.value = [];
  tableData.value = [];
  startDateError.value = '';
  endDateError.value = '';
  pagination.value.page = 1;
  pagination.value.total = 0;
};

const handleRowMouseEnter = async (row, column, cell, event) => {
// 清除上次的定时器
  if (hoverTimeout) {
    clearTimeout(hoverTimeout);
  }

  // 延迟500毫秒再显示图表
  hoverTimeout = setTimeout(async () => {
    if (!row || !row.product || !row.place) {
      console.warn('悬停行缺少必要数据');
      return;
    }
    chartLabel.value = `${row.place}  ${row.product}  行情趋势图`;
    await showPriceChart(row);
  }, 0); // 500毫秒后调用 showPriceChart
};

const showPriceChart = async (row) => {
  try {
    // 获取当前日期
    const currentDate = new Date();

    // 计算开始日期 (当前日期减去11天)
    const startDate = new Date(currentDate);
    startDate.setDate(currentDate.getDate() - 11);

    // 计算结束日期 (当前日期减去1天)
    const endDate = new Date(currentDate);
    endDate.setDate(currentDate.getDate() - 1);

    // 格式化日期为 YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const res = await axios.get('/api/farmOnline/price', {
      params: {
        product: row.product,
        place: row.place,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        pageNum: 0,
        pageSize: 0
      }
    });

    const validItems = res.data.data.items.filter(i => i.price && !isNaN(parseFloat(i.price.replace(/[^\d.]/g, ''))));

    const xData = validItems.map(i => i.time).reverse();
    const yData = validItems.map(i => parseFloat(i.price.replace(/[^\d.]/g, '')).toFixed(2)).reverse();

    showChartDialog.value = true;

    // 等待 DOM 渲染
    await nextTick();

    const chartDom = document.getElementById('priceChart');
    if (chartDom) {
      if (echarts.getInstanceByDom(chartDom)) {
        echarts.getInstanceByDom(chartDom).dispose();
      }
      chartInstance = echarts.init(chartDom);
    }


    chartInstance.setOption({
      tooltip: {
        trigger: 'axis'
      },
      xAxis: {
        type: 'category',
        data: xData
      },
      yAxis: {
        type: 'value',
        name: '价格（元）'
      },
      series: [{
        data: yData,
        type: 'line',
        smooth: true,
        name: '价格趋势',
        lineStyle: { color: '#409EFF' }
      }]
    });

  } catch (e) {
    console.error('加载价格趋势失败：', e);
  }
};

// 页面加载
onMounted(async () => {
  getUserLocation(); // 在页面加载时获取用户位置
  await loadCategories();
});
</script>

<style scoped>
.price-query {
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
}

.filter-form {
  margin-bottom: 20px;
}

.error-message {
  color: #f56c6c;
  font-size: 12px;
  margin-left: 10px;
}

.is-error :deep(.el-input__inner) {
  border-color: #f56c6c;
}
</style>
